import { format } from "date-fns";
import React, { useState } from "react";

import { faFilePdf, faUsers } from "@performyard/fontawesome/pro-solid-svg-icons";
import { Accordion, Alert, Icon, InputWrapper, Text } from "@performyard/py-components";

import { SingleSelectEmployee } from "../../../../components/Inputs/SelectEmployee";

import { type AssigneeValue, type GeneralInfoData, type LaunchTodoRow, REQUIRED_FIELD_MESSAGE } from "../launch-form";

const formatDate = (date: Date | null | undefined): string => {
	if (!date) return "—";
	return format(date, "MMMM d, yyyy");
};

const SummaryField = ({ label, value }: { label: string; value: string }) => (
	<div className="flex flex-col gap-1">
		<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
			{label}
		</Text>
		<Text size="paragraph-3" color="neutral-9">
			{value}
		</Text>
	</div>
);

/**
 * The app loads Tailwind without preflight, so boxes are `content-box`: `w-full` plus any
 * horizontal padding renders wider than its container, and a fixed height plus vertical
 * padding renders taller than the height. These rows therefore take their width from the
 * block flow (no `w-full`) and set either a height or vertical padding, never both.
 */
const TodoSummaryRow = ({ row }: { row: LaunchTodoRow }) => (
	<div className="flex h-10 items-center gap-2 rounded-1 bg-neutral-1 px-3">
		<div className="flex size-4 shrink-0 items-center justify-center">
			{row.signatureTemplate && <Icon icon={faFilePdf} size={16} color="primary-5" />}
		</div>
		<Text size="paragraph-3" color="neutral-9" wrap={false} dangerouslySetClassName="min-w-0 py-text-truncate">
			{row.name || "Untitled to-do"}
		</Text>
	</div>
);

type PreviewStepProps = {
	generalInfo: GeneralInfoData | undefined;
	selectedCount: number;
	rows: LaunchTodoRow[];
	hasManagerAssigned: boolean;
	backupManager: AssigneeValue | null;
	onBackupManagerChange: (value: AssigneeValue | null) => void;
	showBackupManagerError: boolean;
	launchError: string | null;
};

export const PreviewStep = ({
	generalInfo,
	selectedCount,
	rows,
	hasManagerAssigned,
	backupManager,
	onBackupManagerChange,
	showBackupManagerError,
	launchError,
}: PreviewStepProps) => {
	const [todosOpen, setTodosOpen] = useState(false);

	const subjectLabel = selectedCount === 1 ? "1 Employee" : `${selectedCount} Employees`;

	return (
		<div className="flex w-full flex-col gap-4">
			{launchError && <Alert type="error" message={launchError} />}

			{/* Section spacing is set per-child (as in the review-cycle confirmation step) so the
			    workflow name can sit 16px above the first box while sections stay 24px apart. */}
			<div className="rounded-1 border border-solid border-neutral-2 bg-neutral-0 p-6">
				<Text size="sub-heading" weight="semi-bold" color="neutral-9" dangerouslySetClassName="mb-4">
					{generalInfo?.workflowName || "Untitled Workflow"}
				</Text>

				{/* Two equal columns (the design splits the box down the middle), not a flow. */}
				<div className="grid grid-cols-2 items-start gap-6 rounded-1 bg-neutral-1 p-4">
					<SummaryField label="Workflow Start Date" value={formatDate(generalInfo?.startDate)} />
					<SummaryField label="Template" value={generalInfo?.template?.name ?? "—"} />
				</div>

				{hasManagerAssigned && (
					<div
						className="mt-6 flex flex-col gap-6 rounded-1 bg-neutral-1 p-4"
						data-testid="backup-manager-section"
					>
						<div className="flex flex-col gap-2">
							<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
								Assign a Backup Manager
							</Text>
							<Text size="paragraph-3" weight="regular" color="neutral-9">
								Your workflow includes assignments for an employee&apos;s manager. In the event that any
								employee selected for this workflow does not have a manager, please assign a backup
								employee that will be assigned any manager to-dos or signature requests for only those
								employees currently without an assigned manager.
							</Text>
						</div>
						<InputWrapper
							id="backup-manager"
							label="Backup Manager"
							error={showBackupManagerError ? REQUIRED_FIELD_MESSAGE : undefined}
						>
							<SingleSelectEmployee
								value={backupManager}
								onChange={(employeeId, employeeName) =>
									onBackupManagerChange(
										employeeId && employeeName ? { id: employeeId, name: employeeName } : null
									)
								}
								errored={showBackupManagerError}
								placeholder="Make a selection"
								id="backup-manager"
								data-testid="backup-manager-select"
								ariaLabel="Backup Manager"
							/>
						</InputWrapper>
					</div>
				)}

				<div className="mt-6">
					{/*
					 * `box-border` on the header button: without preflight the button's `w-full`
					 * plus its own `px-4` would push it 32px past this card, and its `min-h-10`
					 * plus `py-3` would render 64px instead of the small size's 40px.
					 */}
					<Accordion
						className="[&>button]:box-border"
						size="small"
						isOpen={todosOpen}
						onToggle={() => setTodosOpen((open) => !open)}
						headerContent={
							<div className="flex items-center gap-2">
								<Text size="paragraph-3" weight="semi-bold" color="neutral-9">
									To-Do&rsquo;s
								</Text>
								<Text size="paragraph-3" color="neutral-9">
									{`(${rows.length})`}
								</Text>
							</div>
						}
						aria-label="Workflow to-dos"
						data-testid="preview-todos-accordion"
					>
						<div className="flex flex-col gap-2" data-testid="preview-todos">
							{rows.map((row) => (
								<TodoSummaryRow key={row._id} row={row} />
							))}
						</div>
					</Accordion>
				</div>

				<div className="mt-6">
					<Text size="paragraph-3" weight="semi-bold" color="neutral-9" dangerouslySetClassName="mb-2">
						Subjects
					</Text>
					<div className="flex items-center gap-2 rounded-1 bg-primary-1 px-3 py-2">
						<Icon icon={faUsers} size={16} color="primary-7" />
						<Text size="paragraph-3" color="neutral-9">
							{subjectLabel}
						</Text>
					</div>
				</div>
			</div>
		</div>
	);
};
